import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Flex, ProgressCircle } from '@adobe/react-spectrum';
import refreshIcon from 'app/assets/icons/S2_Icon_Refresh_20_N.svg';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import ChevronIcon from 'app/assets/svgIcons/ChevronIcon';
import SortIcon from 'app/assets/svgIcons/SortIcon';
import {
  ColumnSortMenu,
  type SortMenuOption
} from 'app/pages/MilestonesGrid/components/ColumnSortMenu';
import {
  fetchChangeLog,
  type ChangeLogItem,
  type ChangeLogPresetId
} from '../../../services/momentsApi';
import type { ColumnDef } from 'utils/csvExport';
import {
  downloadCsv,
  formatIsoDateTime,
  formatRichText
} from 'utils/csvExport';
import { isEmptyHtml } from '../CommentPanel/utils';
import slideoutStyles from '../SlideOutPanel.module.scss';
import milestoneMenuStyles from '../Milestones/MilestoneMenu.module.scss';
import styles from './ChangeLog.module.scss';

type TimeRange = '7d' | '30d' | 'all';

type ChangeLogSortColumn =
  | 'changeType'
  | 'changedBy'
  | 'section'
  | 'phase'
  | 'item'
  | 'changeDate';

/** Table header definitions: From / To have no sort UI. */
type ChangeLogHeaderColumn =
  | {
      key: ChangeLogSortColumn;
      label: string;
      sortable: true;
      sortKind: 'text' | 'date';
    }
  | { key: 'from' | 'to'; label: string; sortable: false };

const TEXT_SORT_OPTIONS: SortMenuOption[] = [
  { key: 'aToZ', label: 'A to Z', group: 'alphabetical', order: 1 },
  { key: 'zToA', label: 'Z to A', group: 'alphabetical', order: 2 }
];

const DATE_SORT_OPTIONS: SortMenuOption[] = [
  {
    key: 'ascending',
    label: 'Sort by Ascending',
    group: 'sort',
    order: 1
  },
  {
    key: 'descending',
    label: 'Sort by Descending',
    group: 'sort',
    order: 2
  }
];

const CHANGE_LOG_TABLE_HEADERS: ChangeLogHeaderColumn[] = [
  {
    key: 'changeType',
    label: 'Change type',
    sortable: true,
    sortKind: 'text'
  },
  {
    key: 'changeDate',
    label: 'Change date',
    sortable: true,
    sortKind: 'date'
  },
  {
    key: 'changedBy',
    label: 'Changed by',
    sortable: true,
    sortKind: 'text'
  },
  { key: 'section', label: 'Section', sortable: true, sortKind: 'text' },
  { key: 'phase', label: 'Phase', sortable: true, sortKind: 'text' },
  { key: 'item', label: 'Item', sortable: true, sortKind: 'text' },
  { key: 'from', label: 'From', sortable: false },
  { key: 'to', label: 'To', sortable: false }
];

const SORTABLE_COLUMN_KEYS = new Set<ChangeLogSortColumn>(
  CHANGE_LOG_TABLE_HEADERS.filter(
    (c): c is ChangeLogHeaderColumn & { sortable: true } => c.sortable
  ).map(c => c.key)
);

function changeLogSortOptionsForColumn(
  key: ChangeLogSortColumn
): SortMenuOption[] {
  const col = CHANGE_LOG_TABLE_HEADERS.find(
    (
      c
    ): c is ChangeLogHeaderColumn & {
      sortable: true;
      sortKind: 'text' | 'date';
    } => c.sortable && c.key === key
  );
  if (!col) {
    return TEXT_SORT_OPTIONS;
  }
  return col.sortKind === 'date' ? DATE_SORT_OPTIONS : TEXT_SORT_OPTIONS;
}

export interface ChangeLogSectionProps {
  issueId: string | undefined;
  isOpen: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

function formatPhaseDisplay(phase: string): string {
  if (!phase || !phase.trim()) {
    return '';
  }
  return phase
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
}

function formatChangeDate(iso: string): string {
  if (!iso) {
    return '—';
  }
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return format(d, 'MMM d, yyyy h:mm a');
  } catch {
    return iso;
  }
}

/** "pat" -> "PAT", "mcp" -> "MCP" — undefined/unrecognized values render nothing (direct browser session, the common case, is not distinctly labeled). */
function formatAuthViaSuffix(authVia: string | undefined): string {
  if (authVia === 'mcp') {
    return ' via MCP';
  }
  if (authVia === 'pat') {
    return ' via PAT';
  }
  return '';
}

function displayCell(value: string | undefined): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return String(value);
}

/** Collapsed cap before Read more (half of Overview headline’s 288px — tighter in the table). */
const MAX_COLLAPSED_RICH_HTML_HEIGHT_PX = 144;

type ChangeLogRowVariant = 'white' | 'stripe';

/** Overview-style rich HTML with Read more / Read less when content is tall (shared by From / To cells). */
function ChangeLogRichHtmlCell({
  html,
  rowVariant
}: {
  html: string;
  rowVariant: ChangeLogRowVariant;
}) {
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    const el = contentRef.current;
    if (el) {
      setContentHeight(el.scrollHeight);
    }
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [html, measure]);

  useEffect(() => {
    setExpanded(false);
  }, [html]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const needsReadMore = contentHeight > MAX_COLLAPSED_RICH_HTML_HEIGHT_PX;
  const isCollapsed = needsReadMore && !expanded;

  const collapsedFadeClass =
    rowVariant === 'stripe'
      ? styles.richTextCellCollapsedStripe
      : styles.richTextCellCollapsedWhite;

  return (
    <div className={styles.richTextCellWrapper}>
      <div
        ref={contentRef}
        className={`${styles.richTextCell} ${
          isCollapsed
            ? `${styles.richTextCellCollapsed} ${collapsedFadeClass}`
            : ''
        }`}
        style={
          isCollapsed
            ? {
                maxHeight: `${MAX_COLLAPSED_RICH_HTML_HEIGHT_PX}px`,
                overflow: 'hidden'
              }
            : undefined
        }
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {needsReadMore && (
        <button
          type="button"
          className={styles.readMoreButton}
          onClick={e => {
            e.stopPropagation();
            setExpanded(v => !v);
          }}
          aria-expanded={expanded}
          aria-label={expanded ? 'Read less' : 'Read more'}
        >
          {expanded ? 'Read Less' : 'Read More'}
        </button>
      )}
    </div>
  );
}

/**
 * Single entry point for From / To: empty → em dash; else reuse {@link ChangeLogRichHtmlCell}.
 * When the opposite side has content (e.g. empty `from` paired with non-empty `to`), the empty
 * side is semantically "not set before" (first-time add / clear) rather than "data lost" — we
 * render {@code (empty)} in muted text so users can tell the difference at a glance.
 */
function ChangeLogRichHtmlField({
  value,
  counterpart,
  rowVariant
}: {
  value: string | undefined;
  counterpart?: string | undefined;
  rowVariant: ChangeLogRowVariant;
}): JSX.Element {
  const thisEmpty = !value || isEmptyHtml(value);
  const otherHasContent = !!counterpart && !isEmptyHtml(counterpart);
  if (thisEmpty) {
    // When the counterpart has content, this side represents a first-time add (from=∅→to) or
    // a clear (from→to=∅). Show a placeholder-style pill so the user can tell it apart from a
    // true no-op row (which renders a plain em-dash).
    return otherHasContent ? (
      <span
        className={styles.emptyLabel}
        title="Not previously set"
        aria-label="Not set"
      >
        not set
      </span>
    ) : (
      <>—</>
    );
  }
  return (
    <ChangeLogRichHtmlCell html={value as string} rowVariant={rowVariant} />
  );
}

function compareText(a: string, b: string, aToZ: boolean): number {
  const sa = (a || '').toLocaleLowerCase();
  const sb = (b || '').toLocaleLowerCase();
  const c = sa.localeCompare(sb, undefined, { sensitivity: 'base' });
  return aToZ ? c : -c;
}

function parseDateMs(s: string | undefined): number {
  if (!s || !String(s).trim()) {
    return NaN;
  }
  const t = Date.parse(s);
  return Number.isNaN(t) ? NaN : t;
}

function compareDateMs(aMs: number, bMs: number, ascending: boolean): number {
  const aBad = Number.isNaN(aMs);
  const bBad = Number.isNaN(bMs);
  if (aBad && bBad) {
    return 0;
  }
  if (aBad) {
    return 1;
  }
  if (bBad) {
    return -1;
  }
  return ascending ? aMs - bMs : bMs - aMs;
}

function sortChangeLogItems(
  items: ChangeLogItem[],
  column: ChangeLogSortColumn,
  optionKey: string
): ChangeLogItem[] {
  return [...items].sort((a, b) => {
    switch (column) {
      case 'changeType':
        return compareText(a.changeType, b.changeType, optionKey === 'aToZ');
      case 'changedBy':
        return compareText(a.changedBy, b.changedBy, optionKey === 'aToZ');
      case 'section':
        return compareText(
          a.section ?? '',
          b.section ?? '',
          optionKey === 'aToZ'
        );
      case 'phase':
        return compareText(
          formatPhaseDisplay(a.phase),
          formatPhaseDisplay(b.phase),
          optionKey === 'aToZ'
        );
      case 'item':
        return compareText(
          a.item ?? a.feature?.name ?? '',
          b.item ?? b.feature?.name ?? '',
          optionKey === 'aToZ'
        );
      case 'changeDate':
        return compareDateMs(
          parseDateMs(a.changeDate),
          parseDateMs(b.changeDate),
          optionKey === 'ascending'
        );
      default:
        return 0;
    }
  });
}

function ariaSortForColumn(
  column: ChangeLogSortColumn,
  activeColumn: ChangeLogSortColumn,
  optionKey: string
): 'ascending' | 'descending' | undefined {
  if (activeColumn !== column) {
    return undefined;
  }
  if (optionKey === 'aToZ' || optionKey === 'ascending') {
    return 'ascending';
  }
  if (optionKey === 'zToA' || optionKey === 'descending') {
    return 'descending';
  }
  return undefined;
}

/**
 * Maps UI segment to API `preset`. For "Last 7 days", omit preset so the server default applies
 * (same window as LAST_7_DAYS when sent explicitly).
 */
function timeRangeToPreset(range: TimeRange): ChangeLogPresetId | undefined {
  switch (range) {
    case '7d':
      return undefined;
    case '30d':
      return 'LAST_MONTH';
    case 'all':
    default:
      return 'ALL_TIME';
  }
}

function emptyChangeLogMessage(timeRange: TimeRange): string {
  switch (timeRange) {
    case '7d':
      return 'No change log entries in the last 7 days.';
    case '30d':
      return 'No change log entries in the last month.';
    case 'all':
    default:
      return 'No change log entries for this roadmap item.';
  }
}

/**
 * Defensive: change log rows may still carry JSON-encoded objects in from/to
 * (e.g. legacy `{"id":"ARM-…","name":"…"}` for parent/event, or `{link, date}` for demo/prd
 * on old backend-written FEATURE_UPDATE rows). Surface a human label without widening the cell.
 * The backend already beautifies most of these — this is a fallback for rows it missed.
 */
function beautifyMaybeJsonValue(value: string | undefined): string | undefined {
  if (!value) {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith('{')) {
    return value;
  }
  // 1. Native JSON (parent/event newer writes): extract name-ish key.
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown> | null;
    if (parsed && typeof parsed === 'object') {
      // Bail out on bulk diffs (milestone_fields / decision_fields).
      const hasNestedObject = Object.values(parsed).some(
        v => v !== null && typeof v === 'object'
      );
      if (!hasNestedObject) {
        const labelKey = [
          'momentName',
          'eventName',
          'name',
          'title',
          'displayName',
          'nameOfRelease',
          'label'
        ].find(
          k => typeof parsed[k] === 'string' && (parsed[k] as string).trim()
        );
        if (labelKey) {
          const label = (parsed[labelKey] as string).trim();
          const link = typeof parsed.link === 'string' ? parsed.link : '';
          const date = typeof parsed.date === 'string' ? parsed.date : '';
          if (link && date) {
            return `${label} (${date})`;
          }
          return label;
        }
        // `{link, date}` without a name key — format inline.
        const link = typeof parsed.link === 'string' ? parsed.link : '';
        const date = typeof parsed.date === 'string' ? parsed.date : '';
        if (link && date) return `${link} (${date})`;
        if (link) return link;
        if (date) return date;
      }
    }
  } catch {
    /* fall through to Java HashMap form */
  }
  // 2. Java HashMap.toString() form `{link=..., date=...}` (older backend-written rows).
  const javaMap = trimmed.match(/^\{([^}]+)\}$/);
  if (javaMap) {
    const linkMatch = javaMap[1].match(/link=([^,]+)/);
    const dateMatch = javaMap[1].match(/date=([^,}]+)/);
    const link = linkMatch ? linkMatch[1].trim() : '';
    const date = dateMatch ? dateMatch[1].trim() : '';
    if (link && date) return `${link} (${date})`;
    if (link) return link;
    if (date) return date;
  }
  return value;
}

/** CSV columns aligned with the change log table (ISO date-time for machine-friendly exports). */
const CHANGE_LOG_CSV_COLUMNS: ColumnDef<ChangeLogItem>[] = [
  { key: 'changeType', header: 'Change type' },
  {
    key: 'changeDate',
    header: 'Change date',
    format: formatIsoDateTime
  },
  {
    key: 'changedBy',
    header: 'Changed by',
    value: row => `${row.changedBy}${formatAuthViaSuffix(row.authVia)}`
  },
  {
    key: 'section',
    header: 'Section',
    value: row => row.section ?? ''
  },
  {
    key: 'phase',
    header: 'Phase',
    value: row => formatPhaseDisplay(row.phase)
  },
  {
    key: 'item',
    header: 'Item',
    value: row => {
      const itemLabel = row.item || row.feature?.name || '';
      const productSuffix =
        row.feature?.product && row.feature.product !== itemLabel
          ? ` · ${row.feature.product}`
          : '';
      return itemLabel ? `${itemLabel}${productSuffix}` : '';
    }
  },
  {
    key: 'from',
    header: 'From',
    value: row => beautifyMaybeJsonValue(row.from) ?? '',
    format: formatRichText
  },
  {
    key: 'to',
    header: 'To',
    value: row => beautifyMaybeJsonValue(row.to) ?? '',
    format: formatRichText
  },
  {
    key: 'link',
    header: 'Link',
    value: row => row.link ?? ''
  }
];

/** Stable row identity for React keys (order-independent; not sort index). */
function changeLogRowKey(row: ChangeLogItem): string {
  return [
    row.changeTypeCode ?? row.changeType,
    row.changeDate,
    row.changedBy,
    row.section ?? '',
    row.phase,
    row.item ?? '',
    row.from,
    row.to,
    row.feature?.id ?? '',
    row.feature?.name ?? '',
    row.link ?? ''
  ].join('\u001f');
}

export function ChangeLogSection({
  issueId,
  isOpen,
  isExpanded,
  onToggle
}: ChangeLogSectionProps) {
  const [rawItems, setRawItems] = useState<ChangeLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] =
    useState<ChangeLogSortColumn>('changeDate');
  const [sortOptionKey, setSortOptionKey] = useState<string>('descending');
  const [openSortColumnKey, setOpenSortColumnKey] = useState<string | null>(
    null
  );
  const sortIconRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const tableOverlayRootRef = useRef<HTMLDivElement>(null);
  const loadRequestIdRef = useRef(0);
  /** Drives `preset` on GET .../change-log (server applies the window). */
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const exportMenuButtonRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    if (!issueId) {
      setRawItems([]);
      setLoading(false);
      return;
    }
    const reqId = ++loadRequestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchChangeLog(
        issueId,
        timeRangeToPreset(timeRange)
      );
      if (reqId !== loadRequestIdRef.current) {
        return;
      }
      if (result.success && result.data) {
        setRawItems(result.data.items || []);
      } else {
        setRawItems([]);
        setError(result.message || 'Could not load change log.');
      }
    } catch (e) {
      if (reqId !== loadRequestIdRef.current) {
        return;
      }
      setRawItems([]);
      setError(e instanceof Error ? e.message : 'Could not load change log.');
    } finally {
      if (reqId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [issueId, timeRange]);

  // `load` is memoised on [issueId, timeRange], so timeRange changes already refresh via `load` — do not duplicate in deps.
  useEffect(() => {
    if (!isOpen || !issueId) {
      return;
    }
    void load();
  }, [isOpen, issueId, load]);

  const rows = useMemo(
    () => sortChangeLogItems(rawItems, sortColumn, sortOptionKey),
    [rawItems, sortColumn, sortOptionKey]
  );

  const rowReactKeys = useMemo(() => {
    const seen = new Map<string, number>();
    return rows.map(row => {
      const base = changeLogRowKey(row);
      const n = seen.get(base) ?? 0;
      seen.set(base, n + 1);
      return n === 0 ? base : `${base}__${n}`;
    });
  }, [rows]);

  const toggleSortMenu = useCallback((columnKey: string) => {
    setOpenSortColumnKey(prev => (prev === columnKey ? null : columnKey));
  }, []);

  const closeSortMenu = useCallback(() => {
    setOpenSortColumnKey(null);
  }, []);

  const handleSortOption = useCallback(
    (columnKey: string, optionKey: string) => {
      if (!SORTABLE_COLUMN_KEYS.has(columnKey as ChangeLogSortColumn)) {
        return;
      }
      setSortColumn(columnKey as ChangeLogSortColumn);
      setSortOptionKey(optionKey);
      setOpenSortColumnKey(null);
    },
    []
  );

  const getSortSelections = useCallback(
    (columnKey: string): Set<string> => {
      if (sortColumn === columnKey) {
        return new Set([sortOptionKey]);
      }
      return new Set();
    },
    [sortColumn, sortOptionKey]
  );

  const handleExportCsv = useCallback(() => {
    setExportMenuOpen(false);
    if (!issueId) {
      return;
    }
    const stamp = format(new Date(), 'yyyy-MM-dd');
    downloadCsv(rows, CHANGE_LOG_CSV_COLUMNS, {
      filename: `changelog-${issueId}-${timeRange}-${stamp}`
    });
  }, [issueId, rows, timeRange]);

  useEffect(() => {
    if (!exportMenuOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (exportMenuRef.current?.contains(target)) {
        return;
      }
      if (exportMenuButtonRef.current?.contains(target)) {
        return;
      }
      setExportMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [exportMenuOpen]);

  return (
    <div className={slideoutStyles.section} data-testid="change-log-section">
      <div
        className={`${slideoutStyles.sectionHeader} ${
          isExpanded ? slideoutStyles.sectionHeaderExpanded : ''
        }`}
        style={{ cursor: 'default' }}
      >
        <div className={styles.headerRow}>
          <div className={styles.headerLeft}>
            <div
              className={styles.titleClickable}
              onClick={onToggle}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle();
                }
              }}
            >
              <div className={slideoutStyles.phaseToggle}>
                <ChevronIcon isExpanded={isExpanded} />
              </div>
              <span className={slideoutStyles.sectionTitle}>Change Log</span>
            </div>
            <span className={styles.readOnlyBadge}>Read Only</span>
          </div>
          <div className={styles.headerRight}>
            <div
              className={styles.changeLogToolbar}
              data-testid="change-log-toolbar"
            >
              <button
                type="button"
                className={styles.refreshButton}
                aria-label="Refresh change log"
                onClick={() => load()}
                data-testid="change-log-refresh"
              >
                <img
                  src={refreshIcon}
                  alt=""
                  className={styles.refreshButtonIcon}
                  width={20}
                  height={20}
                  aria-hidden
                />
                <span>Refresh</span>
              </button>
              <div
                className={styles.segmented}
                role="group"
                aria-label="Change log time range"
                data-testid="change-log-time-range"
              >
                <button
                  type="button"
                  className={`${styles.segment} ${
                    timeRange === '7d' ? styles.segmentSelected : ''
                  }`}
                  onClick={() => setTimeRange('7d')}
                  data-testid="change-log-time-range-7d"
                >
                  Last 7 Days
                </button>
                <button
                  type="button"
                  className={`${styles.segment} ${
                    timeRange === '30d' ? styles.segmentSelected : ''
                  }`}
                  onClick={() => setTimeRange('30d')}
                  data-testid="change-log-time-range-30d"
                >
                  Last Month
                </button>
                <button
                  type="button"
                  className={`${styles.segment} ${
                    timeRange === 'all' ? styles.segmentSelected : ''
                  }`}
                  onClick={() => setTimeRange('all')}
                  data-testid="change-log-time-range-all"
                >
                  All Time
                </button>
              </div>
              <div className={milestoneMenuStyles.milestoneMenuContainer}>
                <button
                  ref={exportMenuButtonRef}
                  type="button"
                  className={milestoneMenuStyles.menuButton}
                  onClick={() => setExportMenuOpen(o => !o)}
                  aria-label="Change log options"
                  aria-expanded={exportMenuOpen}
                  aria-haspopup="true"
                  data-testid="change-log-toolbar-menu"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <circle cx="8" cy="3" r="1.5" fill="currentColor" />
                    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
                    <circle cx="8" cy="13" r="1.5" fill="currentColor" />
                  </svg>
                </button>
                {exportMenuOpen && (
                  <div
                    ref={exportMenuRef}
                    className={milestoneMenuStyles.dropdownMenu}
                    data-testid="change-log-export-menu"
                  >
                    <button
                      type="button"
                      className={milestoneMenuStyles.menuItem}
                      onClick={handleExportCsv}
                      disabled={!issueId}
                    >
                      <span className={milestoneMenuStyles.menuItemText}>
                        Export CSV
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div
          className={slideoutStyles.sectionContent}
          data-testid="change-log-section-content"
        >
          {loading && (
            <Flex alignItems="center" gap="size-100">
              <ProgressCircle
                size="S"
                aria-label="Loading change log"
                isIndeterminate
              />
              <span className={styles.loadingState}>Loading…</span>
            </Flex>
          )}
          {!loading && error && (
            <div className={styles.errorState}>{error}</div>
          )}
          {!loading && !error && (
            <div ref={tableOverlayRootRef} className={styles.tableOverlayRoot}>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      {CHANGE_LOG_TABLE_HEADERS.map(col =>
                        col.sortable ? (
                          <th
                            key={col.key}
                            className={
                              col.key === 'changeDate'
                                ? `${styles.th} ${styles.thChangeDate}`
                                : styles.th
                            }
                            data-menu-open={
                              openSortColumnKey === col.key ? 'true' : undefined
                            }
                            data-sort-active={
                              sortColumn === col.key ? 'true' : undefined
                            }
                            aria-sort={ariaSortForColumn(
                              col.key,
                              sortColumn,
                              sortOptionKey
                            )}
                          >
                            <div className={styles.headerContent}>
                              <span>{col.label}</span>
                              <div
                                ref={el => {
                                  sortIconRefs.current[col.key] = el;
                                }}
                                style={{ display: 'inline-block' }}
                              >
                                <SortIcon
                                  className="sort-icon"
                                  onClick={() => toggleSortMenu(col.key)}
                                />
                              </div>
                            </div>
                          </th>
                        ) : (
                          <th key={col.key} className={styles.th}>
                            {col.label}
                          </th>
                        )
                      )}
                      <th className={styles.th}>Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr className={styles.rowWhite}>
                        <td
                          className={styles.td}
                          colSpan={CHANGE_LOG_TABLE_HEADERS.length + 1}
                        >
                          <div className={styles.emptyState}>
                            {emptyChangeLogMessage(timeRange)}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      rows.map((row, idx) => {
                        const rowVariant: ChangeLogRowVariant =
                          idx % 2 === 0 ? 'white' : 'stripe';
                        // Collapse feature product (when available) into the Item cell as a muted
                        // suffix so grouping/scanning stays easy without a dedicated Product column.
                        const itemLabel = row.item || row.feature?.name || '';
                        const productSuffix =
                          row.feature?.product &&
                          row.feature.product !== itemLabel
                            ? ` · ${row.feature.product}`
                            : '';
                        return (
                          <tr
                            key={rowReactKeys[idx]}
                            className={
                              idx % 2 === 0 ? styles.rowWhite : styles.rowStripe
                            }
                          >
                            <td
                              className={`${styles.td} ${styles.tdChangeType}`}
                            >
                              {displayCell(row.changeType) || '—'}
                            </td>
                            <td
                              className={`${styles.td} ${styles.tdValue} ${styles.tdChangeDate}`}
                            >
                              {formatChangeDate(row.changeDate)}
                            </td>
                            <td className={`${styles.td} ${styles.tdValue}`}>
                              {displayCell(row.changedBy)
                                ? `${row.changedBy}${formatAuthViaSuffix(
                                    row.authVia
                                  )}`
                                : '—'}
                            </td>
                            <td
                              className={`${styles.td} ${styles.tdValue} ${styles.tdCompact}`}
                            >
                              {displayCell(row.section) || '—'}
                            </td>
                            <td
                              className={`${styles.td} ${styles.tdValue} ${styles.tdCompact}`}
                            >
                              {formatPhaseDisplay(row.phase) || '—'}
                            </td>
                            <td
                              className={`${styles.td} ${styles.tdValue} ${styles.tdCompact}`}
                              title={row.feature?.product || undefined}
                            >
                              {itemLabel ? `${itemLabel}${productSuffix}` : '—'}
                            </td>
                            <td className={`${styles.td} ${styles.tdValue}`}>
                              <ChangeLogRichHtmlField
                                value={beautifyMaybeJsonValue(row.from)}
                                counterpart={beautifyMaybeJsonValue(row.to)}
                                rowVariant={rowVariant}
                              />
                            </td>
                            <td className={`${styles.td} ${styles.tdValue}`}>
                              <ChangeLogRichHtmlField
                                value={beautifyMaybeJsonValue(row.to)}
                                counterpart={beautifyMaybeJsonValue(row.from)}
                                rowVariant={rowVariant}
                              />
                            </td>
                            <td className={`${styles.td} ${styles.tdValue}`}>
                              {row.link ? (
                                row.link.startsWith('/') ? (
                                  <Link
                                    to={row.link}
                                    className={styles.linkCell}
                                  >
                                    Link
                                  </Link>
                                ) : (
                                  <a
                                    href={row.link}
                                    className={styles.linkCell}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Link
                                  </a>
                                )
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {openSortColumnKey &&
                SORTABLE_COLUMN_KEYS.has(
                  openSortColumnKey as ChangeLogSortColumn
                ) && (
                  <ColumnSortMenu
                    positionMode="overlayRootAbsolute"
                    overlayRootRef={tableOverlayRootRef}
                    columnKey={openSortColumnKey}
                    options={changeLogSortOptionsForColumn(
                      openSortColumnKey as ChangeLogSortColumn
                    )}
                    selectedOptions={getSortSelections(openSortColumnKey)}
                    onOptionToggle={handleSortOption}
                    isOpen
                    onClose={closeSortMenu}
                    anchorElement={
                      sortIconRefs.current[openSortColumnKey] ?? null
                    }
                  />
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
